import { forwardRef } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import * as tokens from '@tokens';
import type { CheckboxInputProps } from '@components/Inputs/Checkbox/CheckboxInput';
import { CheckboxInput } from '@components/Inputs/Checkbox/CheckboxInput';
import { RadioInput } from '@components/Inputs/Radio/RadioInput';
import type { PlanBadgeProps } from '@components/PlanBadge/PlanBadge';
import { PlanBadge } from '@components/PlanBadge/PlanBadge';
import type { IconName } from '@components/Icon/BaseIcon';
import { Icon } from '@components/Icon/Icon';
import { Text } from '@components/Text/Text';
import type { BadgeProps } from '@components/Badge/Badge';
import { Badge } from '@components/Badge/Badge';

interface BaseOptionCardProps
  extends Omit<CheckboxInputProps, 'isIndeterminate'> {
  /**
   * Single-select would render radio buttons and multi-select - checkboxes.
   */
  kind: 'single-select' | 'multi-select';
  /**
   * Clear, concise copy that represents the option.
   */
  title: string;
  /**
   * When supplemental information is needed to help the user make the appropriate choice.
   */
  description?: string;
  /**
   * When provided the respective plan badge would be rendered.
   */
  plan?: PlanBadgeProps['kind'];
  /**
   * When provided the respective badge would be rendered
   */
  badge?: BadgeProps['kind'];
}

interface OptionCardWithIconProps extends BaseOptionCardProps {
  /**
   * OptionCard icon - When provided the respective icon would be rendered.
   */
  icon?: IconName;
  illustration?: never;
}

interface OptionCardWithIllustrationProps extends BaseOptionCardProps {
  /**
   * OptionCard illustration - When provided the respective img would be rendered.
   */
  illustration?: string;
  icon?: never;
}

export type OptionCardProps =
  | OptionCardWithIconProps
  | OptionCardWithIllustrationProps;

/**
 * OptionCard component.
 *
 * {@link https://zeroheight.com/76c439891/p/990c58-option-card Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-option-card--basic-option-card Code documentation}
 *
 * @example
 * <OptionCard />
 */

const Card = styled.label<{ checked: boolean; isDisabled: boolean }>`
  ${tokens.flexCenter()};
  flex-direction: column;
  padding: ${tokens.spacer.x2};
  border-radius: ${tokens.radius.m};
  border: 1px solid ${tokens.color.ui_border};
  width: 100%;
  position: relative;
  text-align: center;
  text-transform: initial;
  color: ${tokens.color.text_default};

  &:hover {
    border: 1px solid ${tokens.color.ui_border_alt};
    ${tokens.elevation(1)};
  }

  ${({ checked }) =>
    checked &&
    `
    background-color: ${tokens.color.bg_active};
    border-color: ${tokens.color.ui_border_active};

    &:hover {
      background-color: ${tokens.color.bg_active};
      border-color: ${tokens.color.ui_border_active};
      ${tokens.elevation(1)};
    }
`}

  ${({ isDisabled }) =>
    isDisabled &&
    `
    cursor: not-allowed;
    color: ${tokens.color.text_disabled};
    background-color: ${tokens.color.ui_background};
    border-color: ${tokens.color.ui_disabled};

    &:hover {
      color: ${tokens.color.text_disabled};  
      background-color: ${tokens.color.ui_background};
      border-color: ${tokens.color.ui_disabled};
      ${tokens.elevation(0)};
    }
  `}
`;

const inputControlStyles = css`
  position: absolute;
  bottom: ${tokens.spacer.x2};
  left: auto;
  top: auto;
`;

const Checkbox = styled(CheckboxInput)`
  ${inputControlStyles}
  right: ${tokens.spacer.x1};
`;

const Radio = styled(RadioInput)`
  ${inputControlStyles}
  right: ${tokens.spacer.x2};
`;

const Title = styled(Text)``;

const Description = styled(Text)`
  margin-top: ${tokens.spacer.x0};
`;

const StyledPlanBadge = styled(PlanBadge)`
  margin-top: ${tokens.spacer.x2};
`;

const StyledBadge = styled(Badge)`
  margin-top: ${tokens.spacer.x2};
`;

const StyledIcon = styled(Icon)`
  margin-bottom: ${tokens.spacer.x1};
`;

const Illustration = styled.span`
  margin-bottom: ${tokens.spacer.x1};
  width: 150px;
  height: 100px;
  background-size: contain;
  background-repeat: no-repeat;
  background-blend-mode: multiply;
  background-position: center;
  background-color: inherit;
`;

export const OptionCard = forwardRef<HTMLInputElement, OptionCardProps>(
  (
    {
      kind,
      title,
      description,
      icon,
      illustration,
      plan,
      className,
      isChecked,
      isDisabled,
      badge,
      ...props
    }: OptionCardProps,
    ref,
  ) => {
    return (
      <Card
        data-testid="option-card"
        checked={isChecked}
        isDisabled={isDisabled}
        className={className}
        style={
          plan || badge
            ? null
            : {
                paddingBottom: tokens.spacer.x5,
              }
        }
      >
        {icon ? <StyledIcon icon={icon} size="l" /> : null}
        {!icon && illustration ? (
          <Illustration style={{ backgroundImage: `url('${illustration}')` }} />
        ) : null}
        <Title
          textColor={isDisabled ? 'text_disabled' : 'text_default'}
          textType="subtitle2"
          as="span"
        >
          {title}
        </Title>
        {description ? (
          <Description
            textColor={isDisabled ? 'text_disabled' : 'text_light'}
            textType="caption"
            as="span"
          >
            {description}
          </Description>
        ) : null}
        {kind === 'multi-select' ? (
          <Checkbox
            isChecked={isChecked}
            isDisabled={isDisabled}
            ref={ref}
            {...props}
          />
        ) : (
          <Radio
            isChecked={isChecked}
            isDisabled={isDisabled}
            ref={ref}
            {...props}
          />
        )}
        {plan ? <StyledPlanBadge kind={plan} size="s" /> : null}
        {badge ? <StyledBadge kind={badge} size="s" /> : null}
      </Card>
    );
  },
);

OptionCard.displayName = 'OptionCard';

OptionCard.defaultProps = {
  isChecked: false,
  isDisabled: false,
  isInvalid: false,
  isRequired: false,
  kind: 'single-select',
};
